'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { HomeIcon, RefreshCcwIcon, ServerCrashIcon, ShieldXIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'

const ErrorPage = ({ error, reset }: { error: Error; reset: () => void }) => {
  const t = useTranslations()

  useEffect(() => {
    console.error(error)
  }, [error])

  const isForbidden =
    error.message?.includes('403') || error.message?.includes('Forbidden') || error.name === 'ForbiddenError'
  const errorCode = isForbidden ? '403' : '500'
  const errorTitle = isForbidden ? t('forbidden_access') : t('server_error')
  const errorDescription = isForbidden ? t('do_not_hove_authority') : t('server_error_description')

  return (
    <div className="flex min-h-screen items-center px-4 py-12 sm:px-6 md:px-8 lg:px-12 xl:px-16">
      <div className="w-full space-y-6 text-center">
        <div className="space-y-3">
          <div className="mb-4 flex justify-center text-red-500">
            {isForbidden ? <ShieldXIcon className="h-20 w-20" /> : <ServerCrashIcon className="h-20 w-20" />}
          </div>
          <h1 className="animate-bounce text-4xl font-bold tracking-tighter sm:text-5xl">{errorCode}</h1>
          <h2 className="mb-5 text-2xl font-semibold tracking-tight sm:text-3xl">{errorTitle}</h2>
          <p className="text-gray-500">{errorDescription}</p>
          {error.message && !isForbidden && <p className="text-red-500">{t('something_went_wrong')}</p>}
        </div>
        <div className="flex items-center justify-center gap-x-6">
          <Link
            href="/"
            onClick={e => {
              e.preventDefault()
              window.location.href = '/'
            }}
            className="inline-flex h-9 items-center rounded-md bg-gray-900 px-6 text-sm font-medium text-gray-50 shadow transition-colors hover:bg-gray-900/90 focus-visible:ring-1 focus-visible:ring-gray-950 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-50/90 dark:focus-visible:ring-gray-300">
            <HomeIcon className="mr-2 h-4 w-4" />
            {t('return_to_home')}
          </Link>
          <Button variant="secondary" onClick={() => reset()}>
            <RefreshCcwIcon className="mr-2 h-4 w-4" />
            {t('try_again')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ErrorPage
